/**
 * AJAR notification helpers — wraps `sonner` for consistent UX and
 * provides drop-in async replacements for the browser primitives that are
 * silently blocked inside sandboxed iframes (e.g. the Emergent preview).
 *
 *  - `toast`              : re-exported from sonner; use directly.
 *  - `confirmToast`       : Promise<boolean> replacement for `window.confirm`.
 *  - `softDeleteWithUndo` : optimistic delete with an undo window.
 *
 * For `window.prompt` use `<RecipientPromptModal>` — there is no clean
 * one-liner equivalent.
 */
import { toast } from "sonner";

export { toast };

/**
 * Show a confirmation toast with action/cancel buttons.
 * Returns a Promise that resolves to `true` if the user confirmed.
 *
 *   if (!(await confirmToast("¿Eliminar este registro?"))) return;
 */
export function confirmToast(message, options = {}) {
  const {
    description,
    confirmLabel = "Sí",
    cancelLabel = "Cancelar",
    duration = 15000,
  } = options;

  return new Promise((resolve) => {
    let settled = false;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      resolve(value);
    };

    toast(message, {
      description,
      duration,
      action: {
        label: confirmLabel,
        onClick: () => settle(true),
      },
      cancel: {
        label: cancelLabel,
        onClick: () => settle(false),
      },
      onDismiss: () => settle(false),
      onAutoClose: () => settle(false),
    });
  });
}

/**
 * Soft-delete UX pattern: caller optimistically removes the item from the UI
 * and calls this helper. If the user clicks "Deshacer" within `duration` ms,
 * `onUndo` runs and the deletion is cancelled. Otherwise (auto-close, manual
 * close) `onDelete` runs to finalise the deletion against the server.
 *
 *   setRows((prev) => prev.filter((r) => r.id !== row.id));
 *   softDeleteWithUndo({
 *     message: "Registro eliminado",
 *     description: `${row.date} · ${row.category}`,
 *     onUndo: () => setRows((prev) => insertSorted(prev, row)),
 *     onDelete: async () => { await api.delete(`/x/${row.id}`); },
 *   });
 */
export function softDeleteWithUndo({
  message,
  description,
  onDelete,
  onUndo,
  duration = 5000,
  undoLabel = "Deshacer",
}) {
  let undone = false;
  let finalised = false;
  const finalise = () => {
    if (undone || finalised) return;
    finalised = true;
    Promise.resolve(onDelete?.()).catch(() => {
      /* The caller's `onDelete` is expected to surface its own errors. */
    });
  };
  toast(message, {
    description,
    duration,
    action: {
      label: undoLabel,
      onClick: () => {
        undone = true;
        onUndo?.();
      },
    },
    onAutoClose: finalise,
    onDismiss: finalise,
  });
}
